import React, { useEffect, useState } from 'react';
import { Alert } from '../../../utils/Alert';
import { asList } from '../../../utils/apiList';
import MainLayout from '../../../components/layout/MainLayout';
import AdminSidebar from '../AdminSidebar';
import axios from 'axios';
import { FaBoxOpen, FaPlus } from 'react-icons/fa';
import { IconWrapper } from '../../../components/IconWrapper';
import './PackageMappingPage.css';
import '../Admin.css';

import { API_URL } from '../../../config';
interface Creator {
  email: string;
  username: string;
  role: string;
  id: number;
  created_at: string;
  updated_at: string | null;
}

interface Course {
  id: number;
  name: string;
  description: string;
  level: string;
  duration: number;
}

interface Package {
  id: number;
  name: string;
  description: string;
  created_by: number;
  created_at: string;
  updated_at: string | null;
  creator: Creator;
  courses: Course[];
}

interface Subscription {
  id: number;
  name: string;
  description: string;
  price: number;
  duration_days: number;
  features: string[];
  is_active: boolean;
  created_at: string;
}

interface PackageSubscriptionMapping {
  id: number;
  package_id: number | any;  // Keep for backward compatibility
  subscription_id: number;
  created_at: string;
  package?: Package;
  subscription?: Subscription;
  package_name?: string;
  subscription_name?: string;
  package_ids?: number[];  // Added for new API structure
  packages?: Package[];    // Added for new API structure
  updated_at?: string | null;
}

const PackageMappingPage: React.FC<{ user: any; onLogout: () => void }> = ({ user, onLogout }) => {
  const [packages, setPackages] = useState<Package[]>([]);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [mappings, setMappings] = useState<PackageSubscriptionMapping[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPackages, setSelectedPackages] = useState<number[]>([]);
  const [selectedSubscription, setSelectedSubscription] = useState<number | null>(null);
  const [editingMapping, setEditingMapping] = useState<PackageSubscriptionMapping | null>(null);
  
  // Add delete confirmation state
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    show: boolean;
    mappingId: number | null;
    mappingName: string;
  }>({
    show: false,
    mappingId: null,
    mappingName: ''
  });

  // Add notification state
  const [notification, setNotification] = useState<{
    type: 'success' | 'error' | 'info';
    message: string;
    show: boolean;
  } | null>(null);

  useEffect(() => {
    fetchPackages();
    fetchSubscriptions();
    fetchMappings();
  }, []);

  const fetchPackages = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        Alert.error('Oops...', 'Authentication token not found');
        setLoading(false);
        return;
      }
      const response = await axios.get(`${API_URL}/api/packages/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        
      });
      setPackages(asList(response.data));
    } catch (err) {
      console.error('Error fetching packages:', err);
      Alert.error('Oops...', 'Failed to fetch packages');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptions = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await axios.get(`${API_URL}/api/subscriptions/subscriptions/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      setSubscriptions(asList(response.data));
    } catch (err) {
      console.error('Error fetching subscriptions:', err);
      Alert.error('Oops...', 'Failed to fetch subscriptions');
    }
  };

  const fetchMappings = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await axios.get(`${API_URL}/api/subscription-packages/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      
      console.log('Raw API response data:', response.data);
      
      // Process the data to handle both old and new API structures
      const processedMappings = response.data.map((mapping: any) => {
        console.log('Processing mapping:', mapping);
        
        // Handle the updated API response structure with multiple packages
        const processed = {
          ...mapping,
          // For display in the table, join package names if multiple
          package_name: mapping.packages && mapping.packages.length > 0 
            ? mapping.packages.map((pkg: Package) => pkg.name).join(', ')
            : (mapping.package ? mapping.package.name : 
              (typeof mapping.package_name === 'object' ? mapping.package_name.name : mapping.package_name || 'Multiple Packages')),
          
          subscription_name: mapping.subscription ? mapping.subscription.name : 
                          (typeof mapping.subscription_name === 'object' ? mapping.subscription_name.name : mapping.subscription_name),
          
          // Ensure package_ids is always available
          package_ids: mapping.package_ids || 
            (mapping.package_id ? [typeof mapping.package_id === 'object' ? mapping.package_id.id : mapping.package_id] : []),
            
          // Ensure package_id is available for backward compatibility
          package_id: mapping.package_id || 
            (mapping.package_ids && mapping.package_ids.length > 0 ? mapping.package_ids[0] : null)
        };
        
        console.log('Processed mapping:', processed);
        return processed;
      });
      
      setMappings(processedMappings);
    } catch (err) {
      console.error('Error fetching mappings:', err);
      // Mock data for demonstration if API doesn't exist yet
      setMappings([]);
    }
  };

  const handleCreateMapping = async () => {
    if (!selectedSubscription) {
      Alert.error('Oops...', 'Please select a subscription');
      return;
    }

    if (selectedPackages.length === 0) {
      Alert.error('Oops...', 'Please select at least one package');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      // Prepare the data for API submission (sanitize package_ids)
      const sanitizedPackageIds = selectedPackages.filter((id) => typeof id === 'number' && Number.isFinite(id));
      const mappingData = {
        subscription_id: selectedSubscription,
        package_ids: sanitizedPackageIds
      };
      
      // Use the correct API endpoint
      await axios.post(`${API_URL}/api/subscription-packages/bulk`, mappingData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      setShowCreateModal(false);
      setSelectedSubscription(null);
      setSelectedPackages([]);
      fetchMappings();
      Alert.success('Success', 'Package subscription mapping created successfully!');
    } catch (err) {
      console.error('Error creating mapping:', err);
      Alert.error('Oops...', 'Failed to create mapping');
      Alert.error('Error', 'Failed to create mapping');
    }
  };

  const handleEditMapping = async () => {
    if (!selectedSubscription) {
      Alert.error('Oops...', 'Please select a subscription');
      return;
    }

    if (selectedPackages.length === 0) {
      Alert.error('Oops...', 'Please select at least one package');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      // Prepare the data for API submission (sanitize package_ids)
      const sanitizedPackageIds = selectedPackages.filter((id) => typeof id === 'number' && Number.isFinite(id));
      const mappingData = {
        subscription_id: selectedSubscription,
        package_ids: sanitizedPackageIds
      };
      
      // Update the existing mapping via PUT (backend supports updating subscription_id and package_ids)
      if (editingMapping) {
        await axios.put(`${API_URL}/api/subscription-packages/${editingMapping.id}`, mappingData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        });
      }
      
      setShowEditModal(false);
      setEditingMapping(null);
      setSelectedSubscription(null);
      setSelectedPackages([]);
      fetchMappings();
      Alert.success('Success', 'Package subscription mapping updated successfully!');
    } catch (err) {
      console.error('Error updating mapping:', err);
      Alert.error('Oops...', 'Failed to update mapping');
      Alert.error('Error', 'Failed to update mapping');
    }
  };

  const handleDeleteClick = (mapping: PackageSubscriptionMapping) => {
    setDeleteConfirmation({
      show: true,
      mappingId: mapping.id,
      mappingName: `${mapping.package_name || 'Package'} → ${mapping.subscription_name || 'Subscription'}`
    });
  };

  const closeDeleteModal = () => {
    setDeleteConfirmation({ show: false, mappingId: null, mappingName: '' });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmation.mappingId) return;

    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      // Use the correct API endpoint
      await axios.delete(`${API_URL}/api/subscription-packages/${deleteConfirmation.mappingId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      fetchMappings();
      closeDeleteModal();
      Alert.success('Success', 'Package subscription mapping deleted successfully!');
    } catch (err) {
      console.error('Error deleting mapping:', err);
      Alert.error('Oops...', 'Failed to delete mapping');
      Alert.error('Error', 'Failed to delete mapping');
    }
  };

  // Handle package selection/deselection
  const togglePackageSelection = (packageId: number) => {
    if (selectedPackages.includes(packageId)) {
      setSelectedPackages(selectedPackages.filter(id => id !== packageId));
    } else {
      setSelectedPackages([...selectedPackages, packageId]);
    }
  };

  // Handle opening the edit modal
  const openEditModal = (mapping: PackageSubscriptionMapping) => {
    setEditingMapping(mapping);
    setSelectedSubscription(mapping.subscription_id);
    
    // Handle multiple package IDs in the new structure
    if (mapping.package_ids && mapping.package_ids.length > 0) {
      // Normalize possible objects to ids and remove invalid entries
      const normalizedIds = mapping.package_ids
        .map((maybeId: any) => (typeof maybeId === 'object' && maybeId !== null ? maybeId.id : maybeId))
        .filter((id: any) => typeof id === 'number' && Number.isFinite(id));
      setSelectedPackages(normalizedIds);
    } else if (typeof mapping.package_id !== 'undefined') {
      // Backward compatibility for old structure
    const packageId = typeof mapping.package_id === 'object' && mapping.package_id !== null
      ? (mapping.package_id as any).id 
      : mapping.package_id;
    
    setSelectedPackages((typeof packageId === 'number' && Number.isFinite(packageId)) ? [packageId] : []);
    } else {
      setSelectedPackages([]);
    }
    
    setShowEditModal(true);
  };

  // Add new function to get unmapped subscriptions
  const getUnmappedSubscriptions = () => {
    // Get all subscription IDs that are already mapped
    const mappedSubscriptionIds = new Set(mappings.map(mapping => mapping.subscription_id));
    
    // Filter out subscriptions that are already mapped
    return subscriptions.filter(subscription => !mappedSubscriptionIds.has(subscription.id));
  };

  // Add new function to get unmapped subscriptions plus current subscription for edit modal
  const getUnmappedSubscriptionsForEdit = (currentSubscriptionId: number) => {
    // Get all subscription IDs that are already mapped
    const mappedSubscriptionIds = new Set(mappings.map(mapping => mapping.subscription_id));
    
    // Remove the current subscription ID from the mapped set
    mappedSubscriptionIds.delete(currentSubscriptionId);
    
    // Filter out subscriptions that are already mapped, except the current one
    return subscriptions.filter(subscription => 
      !mappedSubscriptionIds.has(subscription.id) || subscription.id === currentSubscriptionId
    );
  };

  // Show notification helper function
  const showNotification = (type: 'success' | 'error' | 'info', message: string) => {
    if (type === 'success') {
      Alert.success('Success!', message);
    } else if (type === 'error') {
      Alert.error('Oops...', message);
    } else {
      Alert.warning('Notice', message);
    }
  };

  return (
    <MainLayout user={user} onLogout={onLogout} sidebarContent={<AdminSidebar />}>
      <div className="class-page-root font-kumbh bg-[#FAFBFF] min-h-screen">
        {/* Header Section */}
        <div className="px-8 py-6 flex justify-between items-center">
          <div className="flex flex-col">
            <div className="flex items-center " style={{ gap: 10 }}>
              <h1 className="admin-title" >
                Package Subscription Mapping
              </h1>
            </div>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg bg-[#111827] hover:bg-gray-800 text-white font-kumbh text-base font-semibold shadow-lg transition active:scale-95"
            style={{ fontFamily: 'Inter, sans-serif', boxShadow: '0 8px 24px #0050FF22' }}
            disabled={loading}
          >
            Create New Mapping
          </button>
        </div>

        {/* Main Content */}
        <div className="flex-grow overflow-auto p-8 pt-2">
          <div className="max-w-7xl mx-auto">
            {loading && (
              <div className="flex justify-center py-10">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-lg">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-700 font-kumbh">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {!loading && !error && mappings.length === 0 && (
              <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                <IconWrapper icon={FaBoxOpen} className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h2 className="text-xl font-medium text-gray-600 mb-2">No package subscription mappings available</h2>
                <p className="text-gray-600 mb-6">Create your first package subscription mapping by clicking the button above.</p>
              </div>
            )}

            {!loading && !error && mappings.length > 0 && (
              <div className="bg-white shadow rounded-lg overflow-hidden mt-3">
                <table className="min-w-full divide-y divide-gray-100 font-kumbh" style={{ fontFamily: 'Inter, sans-serif' }}>
                  <thead className="bg-white">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-center text-xs font-bold text-gray-600 uppercase tracking-wider">PACKAGE</th>
                      <th scope="col" className="px-6 py-3 text-center text-xs font-bold text-gray-600 uppercase tracking-wider">SUBSCRIPTION</th>
                      <th scope="col" className="px-6 py-3 text-center text-xs font-bold text-gray-600 uppercase tracking-wider">CREATED AT</th>
                      <th scope="col" className="px-6 py-3 text-center text-xs font-bold text-gray-600 uppercase tracking-wider">ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {mappings.map(mapping => (
                      <tr key={mapping.id} className="hover:bg-gray-50 transition">
                        <td className="px-6 py-3 text-center">
                          <div className="text-sm font-medium text-gray-900">
                            {mapping.packages && mapping.packages.length > 0 ? (
                              <div>
                                <span className="font-semibold">{mapping.packages.length} packages:</span>
                                <ul className="mt-1 space-y-1">
                                  {mapping.packages.slice(0, 3).map((pkg: Package) => (
                                    <li key={pkg.id} className="text-gray-600">{pkg.name}</li>
                                  ))}
                                  {mapping.packages.length > 3 && (
                                    <li className="text-gray-600 text-xs italic">
                                      + {mapping.packages.length - 3} more packages
                                    </li>
                                  )}
                                </ul>
                              </div>
                            ) : (
                              mapping.package_name ||
                              (mapping.package && mapping.package.name) ||
                              'Unknown Package'
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-center">
                          <div className="flex items-center justify-center">
                            <div className="text-sm font-medium text-gray-900">
                              {mapping.subscription_name ||
                                (mapping.subscription && mapping.subscription.name) ||
                                'Unknown Subscription'}
                            </div>
                            {mapping.package_ids && mapping.package_ids.length > 0 && (
                              <span className="ml-2 bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                                {mapping.package_ids.length} packages
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-600 text-center">
                          {new Date(mapping.created_at).toLocaleDateString('en-US', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-center text-sm font-medium">
                          <div className="flex justify-center items-center gap-3">
                            <button
                              onClick={() => openEditModal(mapping)}
                              className="inline-flex items-center px-3 py-1.5 mr-3 text-sm font-medium text-[#111827] bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-gray-700 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              disabled={loading}
                            >
                              <span className="w-4 h-4 mr-1.5">
                                <svg fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                </svg>
                              </span>
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteClick(mapping)}
                              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 hover:text-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              disabled={loading}
                            >
                              <span className="w-4 h-4 mr-1.5">
                                <svg fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                              </span>
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

        {/* Create Mapping Modal */}
        {showCreateModal && (
          <div className="class-modal-overlay">
            <div className="class-modal-box">
              <div className="class-modal-title">Create New Package Subscription Mapping</div>
              <div className="space-y-5">
                {error && (
                  <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-3 rounded shadow-sm text-sm">
                    {error}
                  </div>
                )}
                <div>
                  <label className="class-modal-form-label">Select Subscription</label>
                  <select
                    value={selectedSubscription || ''}
                    onChange={(e) => setSelectedSubscription(e.target.value ? Number(e.target.value) : null)}
                    className="class-modal-input"
                  >
                    <option value="">-- Select a subscription --</option>
                    {getUnmappedSubscriptions().map(sub => (
                      <option key={sub.id} value={sub.id}>
                        {sub.name} (${sub.price} / {sub.duration_days} days)
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="class-modal-form-label">Select Packages</label>
                    <div className="text-sm bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                      {selectedPackages.length} selected
                    </div>
                  </div>
                  <div className="border border-gray-300 rounded-lg max-h-[180px] overflow-y-auto">
                    <div className="bg-gray-50 px-4 py-1.5 text-xs font-medium text-gray-600 uppercase">
                      SELECT PACKAGES TO INCLUDE IN THIS MAPPING
                    </div>
                    <div className="divide-y divide-gray-200">
                      {packages.map(pkg => (
                        <div
                          key={pkg.id}
                          className={`flex items-start p-2.5 hover:bg-gray-50 ${
                            selectedPackages.includes(pkg.id) ? 'bg-blue-50' : ''
                          }`}
                          onClick={() => togglePackageSelection(pkg.id)}
                        >
                          <input
                            type="checkbox"
                            checked={selectedPackages.includes(pkg.id)}
                            onChange={() => togglePackageSelection(pkg.id)}
                            className="mt-1 h-4 w-4 text-[#111827] rounded border-gray-300 focus:ring-gray-700"
                          />
                          <div className="ml-3">
                            <div className="font-medium text-gray-900 text-sm">{pkg.name}</div>
                            <div className="text-gray-600 text-xs mt-0.5 line-clamp-1">{pkg.description}</div>
                            <div className="flex gap-2 mt-1.5">
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                                {pkg.courses.length} courses
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="class-modal-actions">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setSelectedSubscription(null);
                    setSelectedPackages([]);
                  }}
                  className="class-modal-cancel-btn"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateMapping}
                  className="class-modal-save-btn transition"
                  disabled={loading || !selectedSubscription || selectedPackages.length === 0}
                >
                  {loading ? 'Creating...' : 'Create Mapping'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Mapping Modal */}
        {showEditModal && editingMapping && (
          <div className="class-modal-overlay">
            <div className="class-modal-box">
              <div className="class-modal-title">Edit Package Subscription Mapping</div>
              <div className="space-y-5">
                {error && (
                  <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-3 rounded shadow-sm text-sm">
                    {error}
                  </div>
                )}
                <div>
                  <label className="class-modal-form-label">Select Subscription</label>
                  <select
                    value={selectedSubscription || ''}
                    onChange={(e) => setSelectedSubscription(e.target.value ? Number(e.target.value) : null)}
                    className="class-modal-input"
                  >
                    <option value="">-- Select a subscription --</option>
                    {editingMapping && getUnmappedSubscriptionsForEdit(editingMapping.subscription_id).map(sub => (
                      <option key={sub.id} value={sub.id}>
                        {sub.name} (${sub.price} / {sub.duration_days} days)
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="class-modal-form-label">Select Packages</label>
                    <div className="text-sm bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                      {selectedPackages.length} selected
                    </div>
                  </div>
                  <div className="border border-gray-300 rounded-lg max-h-[180px] overflow-y-auto">
                    <div className="bg-gray-50 px-4 py-1.5 text-xs font-medium text-gray-600 uppercase">
                      SELECT PACKAGES TO INCLUDE IN THIS MAPPING
                    </div>
                    <div className="divide-y divide-gray-200">
                      {packages.map(pkg => (
                        <div
                          key={pkg.id}
                          className={`flex items-start p-2.5 hover:bg-gray-50 ${
                            selectedPackages.includes(pkg.id) ? 'bg-blue-50' : ''
                          }`}
                          onClick={() => togglePackageSelection(pkg.id)}
                        >
                          <input
                            type="checkbox"
                            checked={selectedPackages.includes(pkg.id)}
                            onChange={() => togglePackageSelection(pkg.id)}
                            className="mt-1 h-4 w-4 text-[#111827] rounded border-gray-300 focus:ring-gray-700"
                          />
                          <div className="ml-3">
                            <div className="font-medium text-gray-900 text-sm">{pkg.name}</div>
                            <div className="text-gray-600 text-xs mt-0.5 line-clamp-1">{pkg.description}</div>
                            <div className="flex gap-2 mt-1.5">
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                                {pkg.courses.length} courses
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="class-modal-actions">
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingMapping(null);
                    setSelectedSubscription(null);
                    setSelectedPackages([]);
                  }}
                  className="class-modal-cancel-btn"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditMapping}
                  className="class-modal-save-btn transition"
                  disabled={loading || !selectedSubscription || selectedPackages.length === 0}
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmation.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-start">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Delete Package Subscription Mapping
                </h3>
                <div className="mt-2">
                  <p className="text-sm text-gray-600">
                    Are you sure you want to delete <strong>{deleteConfirmation.mappingName}</strong>? This action cannot be undone.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                onClick={handleDeleteConfirm}
                disabled={loading}
                className="w-full inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-red-600 text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm transition active:scale-95"
              >
                {loading ? 'Deleting...' : 'Delete'}
              </button>
              <button
                type="button"
                onClick={closeDeleteModal}
                disabled={loading}
                className="mt-3 w-full inline-flex justify-center rounded-lg border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-600 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-700 sm:mt-0 sm:w-auto sm:text-sm transition active:scale-95"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Popup */}
      {notification && (
        <div className="fixed top-4 right-4 z-50 max-w-sm">
          <div className={`rounded-lg shadow-lg p-4 border-l-4 ${
            notification.type === 'success' 
              ? 'bg-green-50 border-green-400 text-green-800' 
              : notification.type === 'error'
              ? 'bg-red-50 border-red-400 text-red-800'
              : 'bg-blue-50 border-blue-400 text-blue-800'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className={`w-2 h-2 rounded-full mr-3 ${
                  notification.type === 'success' ? 'bg-green-400' 
                  : notification.type === 'error' ? 'bg-red-400' 
                  : 'bg-blue-400'
                }`}></div>
                <p className="text-sm font-medium">{notification.message}</p>
              </div>
              <button
                onClick={() => setNotification(null)}
                className="ml-4 text-gray-400 hover:text-gray-600"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default PackageMappingPage;

